import { fleetPageVersionPath } from 'common/pages/fleetV2/settings/fleetPageRoutes';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router';

export type UserRouterHandlerProps = [
  string,
  {
    path?: string;
    route?: string;
  }
];

export const UseHandlePageRouting = (
  type: string,
  paths: string,
  options?: {
    path?: string;
    route?: string;
    pathKey?: string;
  }
): void => {
  const { pathname } = useLocation();

  const slugsArr = pathname.split('/').filter((slug) => slug !== '');
  const pathsArr = paths.split('/').filter((slug) => slug !== '');

  const pathIndexs: Record<string, number> = pathsArr.reduce(
    (acc, path, index) => (acc = { ...acc, [path]: index }),
    {}
  );

  switch (type) {
    case 'updatePath': {
      if (!options?.path || !pathIndexs?.[options.path])
        return console.error('No path provided in handlePageRouting');
      const newSlugsArr = [...slugsArr];
      const slugIndex = pathIndexs[options.path];
      newSlugsArr[slugIndex] = options.route || '';
      return console.log(newSlugsArr);
    }
    default:
      return console.log('No type provided in handlePageRouting');
  }
};

const convertPathToSlugsObj = (paths: string): [Record<string, string>, string, Record<string, unknown> | undefined] => {
  const { pathname, search } = useLocation();
  const cleanPath = pathname.split('?')[0] 
  const urlSlugs = pathname.split('/').filter((slug) => slug !== '');
  const basePaths = paths.split('/').filter((slug) => slug !== '');
  
  const pathIndex: Record<number, string> = basePaths.reduce(
    (acc, path, index) => (acc = { ...acc, [index]: path }),
    {}
  );

  return [
    // return key value object where the key is the path and the value is the slug
    urlSlugs.reduce((acc, slug, index) => {
      const pathKey = pathIndex[index];
      return (acc = { ...acc, [pathKey]: slug });
    }, {}),
    pathname,
    search?.split('?')?.[1]?.split('&').reduce((acc, item) => {
      const [key, value] = item.split('=');
      return { ...acc, [key]: value };
    }, {})
  ]
};

/*
export const useRouterWithHandlerV2 = (
  paths: string,
  options?: {
    path?: string;
  }
): [Record<string, unknown>, (type: string, options?: Record<string, unknown>) => void, string] => {
  if (options) console.log('options not used in useRouter hook');

  const [slugs, currentUrl, urlVars] = convertPathToSlugsObj(paths);

  const history = useHistory();
  const { pathname } = useLocation();

  const handlePageRouting = (
    type: string,
    paths: string,
    options?: Record<string, unknown>
  ): void => {
    const slugsArr: string[] = pathname.split('/').filter((slug) => slug !== '');
    const pathsArr = paths.split('/').filter((slug) => slug !== '');

    const pathIndexs: Record<string, number> = pathsArr.reduce(
      (acc, path, index) => (acc = { ...acc, [path]: index }),
      {}
    );

    switch (type) {
      // updatePath updates only a single path directory in the URL
      case 'update': {
        if (!options) return console.log('missing key val [path]: slug (value)');

        const [path, slug] = Object.entries(options)[0];

        const newSlugsArr = [...slugsArr];
        const slugIndex = pathIndexs?.[path] as number;

        newSlugsArr[slugIndex] = slug as string;
        const newPath = `/${newSlugsArr.join('/')}`;

        return history.push(newPath);
      }
      // this just sets the whole url to the incoming { path: string } option
      case 'setPath': {
        if (!options?.path) return console.log('missing key val [path]: slug (value)');
        const path = options?.path;
        return history.push(path as string);
      }

      default:
        return console.log('No type provided in handlePageRouting');
    }
  };

  const handle = (type: string, options?: Record<string, unknown>) => {
    handlePageRouting(type, paths, options);
  };

  const slugsToUse = { ...slugs, fleetPageVersionPath };

  return [{ ...slugsToUse, currentUrl, urlVars }, handle, fleetPageVersionPath];
};
*/

export type UseRouterWithHandlerReturnProps = [
  { 
    urlVars: Record<string, string>; 
    currentUrl: string;
    [key: string]: string | unknown;
  },
  (type: string, options?: Record<string, unknown> | string) => string | number | (string | number)[],
  string | undefined
];

export const useRouterWithHandler = (
  paths: string,
  options?: {
    path?: string;
  }): UseRouterWithHandlerReturnProps => {

  if (options) console.log('options not used in useRouter hook');

  const [slugs, currentUrl, urlVars] = convertPathToSlugsObj(paths);

  const history = useHistory();
  const { pathname } = useLocation();
 
  const handlePageRouting = (
    type: string,
    paths: string,
    options?: {
      path?: string,
      urlVars?: Record<string, string | number>,
      [key: string]: unknown
    }
  ): string | undefined | (string | number | Date)[] | number => {

    const pathUpdater = (options: Record<string, unknown>): string => {

      const el = options?.e as Event;
      if (el) el.preventDefault();
      
      const slugsArr: string[] = pathname.split('/').filter((slug: string) => slug !== '');
      const pathsArr = paths.split('/').filter((slug: string) => slug !== '');

      const pathIndexs: Record<string, number> = pathsArr.reduce(
        (acc, path, index) => (acc = { ...acc, [path]: index }),
        {}
      );

      const [pathA, slugA] = Object.entries(options)[0];
      const newSlugsArr = [...slugsArr];
      const slugIndex = pathIndexs?.[pathA] as number;

      if (slugsArr[slugIndex] !== slugA) {
        newSlugsArr[slugIndex] = slugA as string;
      }

      return `/${newSlugsArr.join('/')}`

    };

    
    const { path, vars, ...optionsVars } = options || {}

    switch (type) {
      
      
      case 'setPath':
      case 'set': 
      case 'update':
      case 'updatePath': {
        if (!options) throw new Error('missing options in router handler');
        if (!path) history.push(pathUpdater(options));
        else history.push(options?.path as string);
        break;
      }

      // this will return what the url would look like with update values (used for a links mainly)
      case 'getUpdatedPath': {
        if (!options) throw new Error('missing options in router handler');
        return pathUpdater(options);
      }

      case 'getVar': {
        if (typeof options === 'string') return urlVars?.[options] as string
        break;
      }

      case 'clearVar': {
        const parthVars = new URL(window.location.href).searchParams

        if (typeof options === 'string') {
          parthVars?.delete(options)
        } else {
          const keyToDelete = Object.keys(options|| {})[0]
          parthVars?.delete(keyToDelete)
        }
        
        history.push(pathname);
        // update the url with the new search params
        //history.push(`${pathname.split('?')[0]}?${parthVars.toString()}`)

        break
      }

      // set range adds ?range=startTime,endTime to the url
      case 'setRange': 
      case 'updateVars': {

        let { startTime, endTime, ...optionsVarsRest } = optionsVars as Record<string, string | number | Date>    
        optionsVarsRest = optionsVarsRest || {}
        let [newPath] = pathname.split('?')
        
        let newUrlVars = { ...urlVars, ...optionsVarsRest }

        if (type === 'setRange') {
          if (startTime) startTime = new Date(startTime).getTime()
          if (endTime) endTime = new Date(endTime).getTime()
          if (!startTime || !endTime) throw new Error('missing start or end time in router handler setRange');
          newUrlVars = { ...newUrlVars,  range: `${startTime},${endTime}` }
        } else {
          newUrlVars = {...newUrlVars, ...(vars || {})}
        }

        Object.keys(optionsVarsRest).forEach((key) => {
          if (optionsVarsRest[key] === '' || optionsVarsRest[key] === undefined || optionsVarsRest[key] === 'undefined') delete newUrlVars[key]
        })

        newPath += `?${Object.entries(newUrlVars).map(([key, value]) => `${key}=${value}`).join('&')}`

        history.push(newPath)

        return newPath
        
      }

      case 'getRange': 
      case 'getDates': {
        const range = urlVars?.range as string
        if (!range) break
        const [start, end] = (range.split(',') as (string | number)[] || ['1', '2'])?.map(Number)        

        if (type === 'getDates') return [new Date(start), new Date(end)] as [Date, Date]
        return [start, end]
      }

      

      default:
        console.log('No type provided in handlePageRouting');
        break
    }
  };

  const handle = (type: string, options?: Record<string, unknown> | string): string | number | (string | number | Date)[] => handlePageRouting(type, paths, options) || ''
 
  const slugsToUse = { ...slugs, fleetPageVersionPath };
  return [{ ...slugsToUse, currentUrl, urlVars: (urlVars || {}) as Record<string, string> }, handle, fleetPageVersionPath];
};

export const useRouter = (
  paths: string,
  options?: Record<string, unknown>
): Record<string, string> => {
  if (options) console.log('options not used in useRouter hook');
  const [slugs, currentUrl] = convertPathToSlugsObj(paths);
  return { ...slugs, currentUrl };
};
